import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { MapPin, Award, Briefcase, Globe, Instagram, Youtube, ExternalLink, ArrowLeft } from "lucide-react";

function Chip({ children }) {
  return <span className="inline-flex items-center px-2.5 py-1 rounded-full bg-white/5 border border-white/10 text-xs text-neutral-300">{children}</span>;
}

export default function MemberProfile() {
  const { id } = useParams();
  const [m, setM] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const data = await api.member(id);
        if (alive) setM(data);
      } catch (e) {
        if (alive) setError("Could not load this profile");
      } finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, [id]);

  if (loading) return <div className="text-sm text-neutral-500">Loading profile…</div>;
  if (error || !m) return <div className="text-sm text-red-400">{error || "Not found"}</div>;

  const initials = (m.name || "?").split(" ").map(s => s[0]).slice(0,2).join("").toUpperCase();

  return (
    <div>
      <Link to="/app/directory" className="inline-flex items-center gap-2 text-sm text-neutral-400 hover:text-white mb-6" data-testid="member-back-link">
        <ArrowLeft className="w-4 h-4" /> Back to directory
      </Link>
      <div className="relative rounded-2xl overflow-hidden mb-8 h-40 bg-[#0a0908] border border-white/10">
        {m.banner_url ? (
          <img src={m.banner_url} className="w-full h-full object-cover" alt="" />
        ) : (
          <div className="w-full h-full" style={{ background: "radial-gradient(circle at 20% 30%, rgba(255,90,0,0.35), transparent 60%), #0a0908" }} />
        )}
      </div>
      <div className="flex flex-col md:flex-row items-start gap-6 -mt-16 relative z-10">
        <div className="w-28 h-28 rounded-2xl ff-accent-bg text-black font-semibold flex items-center justify-center text-3xl border-4 border-[#050505] flex-shrink-0">
          {m.avatar_url ? <img src={m.avatar_url} className="w-full h-full rounded-2xl object-cover" alt="" /> : initials}
        </div>
        <div className="flex-1">
          <h1 className="font-display text-3xl sm:text-4xl tracking-tighter leading-none font-medium">{m.name}</h1>
          <div className="text-neutral-400 mt-2">{m.headline || "Southern Utah film"}</div>
          <div className="flex flex-wrap gap-3 mt-3 text-xs uppercase tracking-widest text-neutral-500">
            {m.location && <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" /> {m.location}</span>}
            {m.verified && <span className="inline-flex items-center gap-1 ff-accent-text"><Award className="w-3 h-3" /> Verified</span>}
            {m.available && <span className="inline-flex items-center gap-1"><Briefcase className="w-3 h-3" /> Available</span>}
            {m.union && <span className="inline-flex items-center gap-1">{m.union}</span>}
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6 mt-10">
        <div className="lg:col-span-2 space-y-6">
          {m.bio && (
            <section className="ff-card p-6" data-testid="member-bio">
              <div className="text-xs uppercase tracking-[0.25em] text-neutral-400 mb-3">Bio</div>
              <p className="text-neutral-200 whitespace-pre-wrap leading-relaxed">{m.bio}</p>
            </section>
          )}
          {(m.departments?.length || m.skills?.length || m.specialties?.length) ? (
            <section className="ff-card p-6">
              <div className="text-xs uppercase tracking-[0.25em] text-neutral-400 mb-3">Craft</div>
              {m.departments?.length ? (
                <div className="mb-4">
                  <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-2">Departments</div>
                  <div className="flex flex-wrap gap-2">{m.departments.map((d, i) => <Chip key={i}>{d}</Chip>)}</div>
                </div>
              ) : null}
              {m.skills?.length ? (
                <div className="mb-4">
                  <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-2">Skills</div>
                  <div className="flex flex-wrap gap-2">{m.skills.map((s, i) => <Chip key={i}>{s}</Chip>)}</div>
                </div>
              ) : null}
              {m.specialties?.length ? (
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-2">Specialties</div>
                  <div className="flex flex-wrap gap-2">{m.specialties.map((s, i) => <Chip key={i}>{s}</Chip>)}</div>
                </div>
              ) : null}
            </section>
          ) : null}
          {m.equipment?.length ? (
            <section className="ff-card p-6">
              <div className="text-xs uppercase tracking-[0.25em] text-neutral-400 mb-3">Equipment</div>
              <div className="flex flex-wrap gap-2">{m.equipment.map((s, i) => <Chip key={i}>{s}</Chip>)}</div>
            </section>
          ) : null}
        </div>

        <aside className="space-y-4">
          <section className="ff-card p-6">
            <div className="text-xs uppercase tracking-[0.25em] text-neutral-400 mb-3">Connect</div>
            <div className="space-y-2 text-sm">
              {m.website && <a href={m.website} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-neutral-300 hover:text-white"><Globe className="w-4 h-4 ff-accent-text" /> Website <ExternalLink className="w-3 h-3" /></a>}
              {m.imdb_url && <a href={m.imdb_url} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-neutral-300 hover:text-white"><ExternalLink className="w-4 h-4 ff-accent-text" /> IMDb</a>}
              {m.reel_url && <a href={m.reel_url} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-neutral-300 hover:text-white"><ExternalLink className="w-4 h-4 ff-accent-text" /> Reel</a>}
              {m.instagram && <a href={`https://instagram.com/${m.instagram.replace('@','')}`} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-neutral-300 hover:text-white"><Instagram className="w-4 h-4 ff-accent-text" /> {m.instagram}</a>}
              {m.youtube && <a href={m.youtube} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-neutral-300 hover:text-white"><Youtube className="w-4 h-4 ff-accent-text" /> YouTube</a>}
              {!m.website && !m.imdb_url && !m.reel_url && !m.instagram && !m.youtube && (
                <div className="text-xs text-neutral-500">No public links.</div>
              )}
            </div>
          </section>
          {m.awards?.length ? (
            <section className="ff-card p-6">
              <div className="text-xs uppercase tracking-[0.25em] text-neutral-400 mb-3">Awards</div>
              <ul className="text-sm text-neutral-300 space-y-2">{m.awards.map((a, i) => <li key={i}>{a}</li>)}</ul>
            </section>
          ) : null}
          {m.credits?.length ? (
            <section className="ff-card p-6">
              <div className="text-xs uppercase tracking-[0.25em] text-neutral-400 mb-3">Credits</div>
              <ul className="text-sm text-neutral-300 space-y-2">{m.credits.slice(0, 8).map((c, i) => <li key={i}>{typeof c === "string" ? c : (c.title || c.role || "Credit")}</li>)}</ul>
            </section>
          ) : null}
        </aside>
      </div>
    </div>
  );
}
